import { getFolderDocuments } from "slices/workspace/navigationNodes";

export const filterThemes = (themes, showAll, isSearch, filterView) => {
  return themes.filter((theme) => {
    if (theme.Occurrences <= 0) {
      return false;
    }

    if (theme.NounPhrase && theme.collapsed) {
      return false;
    }

    if (isSearch && !theme.FilteredBySearch) {
      return false;
    }

    if (filterView && !theme.FilteredByFilter) {
      return false;
    }

    // If "Show Checked" mode applied
    if (!showAll) {
      if (theme.NounPhrase && !theme.selected) {
        return false;
      }

      // If group
      const checkedNPsInGroup = themes.filter(
        (t) => t.NounPhrase && t.GroupName === theme.GroupName && t.checked
      );
      if (!checkedNPsInGroup.length) {
        return false;
      }
    }

    return true;
  });
};

export const searchFilter = (isSearch, categories) =>
  isSearch ? categories.filter((t) => t.FilteredBySearch) : categories;

export const filterByFilter = (filterView, categories) =>
  filterView ? categories.filter((t) => t.FilteredByFilter) : categories;

const arrayMinMax = (arr) =>
  arr.reduce(
    ([min, max], val) => [Math.min(min, val), Math.max(max, val)],
    [Number.POSITIVE_INFINITY, Number.NEGATIVE_INFINITY]
  );

export const getMinMaxFreq = (themes, isSearch) => {
  const frequencies = [];
  themes.forEach((t) =>
    t.Documents.forEach((d) =>
      frequencies.push(
        isSearch && !t.NounPhrase ? d.Search.Occurrences : d.Occurrences
      )
    )
  );
  const minMax = arrayMinMax(frequencies);
  return { max: minMax[1], min: minMax[0] };
};

export const hasItems = (index, themeToCheck, state) => {
  // Check if it's the last item
  index = index === state.themes.length - 1 ? index : index + 1;
  return (
    state.themes[index].GroupName === themeToCheck &&
    state.themes[index].NounPhrase
  );
};

export const calculateThemeFrequency = (
  themeList,
  theme,
  showAll,
  isSearch,
  filterView
) => {
  let fq = 0;
  let newThemes = themeList.filter((t) => t.GroupName === theme.GroupName);
  if (!showAll) {
    newThemes = newThemes.filter((t) => t.checked || t.selected);
  }
  if (isSearch) {
    newThemes = newThemes.filter((t) => t.FilteredBySearch);
  }
  if (filterView) {
    newThemes = newThemes.filter((t) => t.FilteredByFilter);
  }

  if (filterView && showAll) {
    fq = theme.SearchOccurrences;
  } else {
    newThemes.forEach((item) => {
      if (item.NounPhrase) {
        fq += item.Occurrences;
      }
    });
  }

  return fq;
};

export const itemHasOccurrencesInDoc = (item, doc) => {
  const document = item.Documents.find((d) => d.Id === doc.Id);
  if (document !== undefined) {
    return document.Occurrences > 0;
  }
  return false;
};

export const calculateThemeFrequencyByDoc = (
  themeList,
  theme,
  showAll,
  isSearch,
  doc
) => {
  let fq = 0;
  let newThemes = themeList.filter((t) => t.GroupName === theme.GroupName);
  if (!showAll) {
    newThemes = newThemes.filter(
      (t) => t.checked && itemHasOccurrencesInDoc(t, doc)
    );
  }
  if (isSearch) {
    newThemes = newThemes.filter(
      (t) => t.FilteredBySearch && itemHasOccurrencesInDoc(t, doc)
    );
  }
  newThemes.forEach((item) => {
    if (item.NounPhrase) {
      const document = item.Documents.find((d) => d.Id === doc.Id);
      fq += document !== undefined ? document.Occurrences : 0;
    }
  });
  return fq;
};

export const getMaxFreq = (paragraphs) => {
  return paragraphs.reduce((maxFreq, paragraph) => {
    const paragraphFreqs = paragraph.items.map((term) => term.freq);
    const paragraphMaxFreq = Math.max(...paragraphFreqs);

    return Math.max(maxFreq, paragraphMaxFreq);
  }, 0);
};

/* On discovery and dictionary search pages need sync categories and documents columns when scrolling one of these.
  Here we can hit infinite cycle, where columns update each other. This functions is created to check if we didn't fall in
  infinite cycle */
export const getIsCycle = () => {
  let prev = null;
  let oneBeforePrev = null;

  return (value) => {
    if (oneBeforePrev === value) {
      return true;
    }

    oneBeforePrev = prev;
    prev = value;

    return false;
  };
};

const hasFolderDocument = (navigationInfo, folderId, predicate) =>
  (getFolderDocuments(navigationInfo, folderId) ?? []).some(predicate);

export const hasWordOrPdfDocs = (folderId, navigationInfo) =>
  hasFolderDocument(
    navigationInfo,
    folderId,
    ({ docType }) => docType === "word" || docType === "pdf"
  );

export const hasExcelDocs = (folderId, navigationInfo) =>
  hasFolderDocument(
    navigationInfo,
    folderId,
    ({ docType }) => docType === "excel"
  );
